import { useRef, useState } from 'react';
import type { JiraAttachment } from '@shared/types.js';
import { AdfRenderer } from '@/components/AdfRenderer.js';
import { useEditorUpload, useUpdateIssueField } from '@/hooks/useIssues.js';
import type { RichEditorHandle } from '@/features/editor/RichEditor.js';
import { LazyRichEditor as RichEditor } from '@/features/editor/LazyRichEditor.js';

interface EditableDescriptionProps {
  issueKey: string;
  projectKey: string;
  descriptionAdf: unknown;
  description: string | null;
  attachments?: JiraAttachment[];
}

/** Issue description: rendered ADF, or the rich editor while editing (like Jira's click-to-edit). */
export function EditableDescription({ issueKey, projectKey, descriptionAdf, description, attachments }: EditableDescriptionProps) {
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const editorRef = useRef<RichEditorHandle>(null);
  const update = useUpdateIssueField();
  const upload = useEditorUpload(issueKey);

  const save = () => {
    const ed = editorRef.current;
    if (!ed || ed.isUploading() || update.isPending) return;
    update.mutate(
      { issueKey, fields: { descriptionAdf: ed.isEmpty() ? null : ed.getAdf() } },
      { onSuccess: () => setEditing(false) },
    );
  };

  const hasDescription = !!(descriptionAdf || description);

  return (
    <section className="mt-6">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800">Description</h3>
        {!editing && (
          <button type="button" onClick={() => setEditing(true)} className="text-xs text-gray-500 hover:text-blue-600">
            Edit
          </button>
        )}
      </div>
      {editing ? (
        <div>
          <RichEditor
            ref={editorRef}
            initialAdf={descriptionAdf ?? (description ? { type: 'doc', version: 1, content: [{ type: 'paragraph', content: [{ type: 'text', text: description }] }] } : null)}
            attachments={attachments}
            projectKey={projectKey}
            onUploadFile={upload}
            onSubmit={save}
            onChange={() => setUploading(!!editorRef.current?.isUploading())}
            placeholder="Add a description… Type @ to mention, paste or drop images and files"
            minHeight={160}
            autoFocus
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={save}
              disabled={uploading || update.isPending}
              className="rounded-md bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {update.isPending ? 'Saving...' : 'Save'}
            </button>
            <button type="button" onClick={() => setEditing(false)} className="rounded-md px-3 py-1 text-sm text-gray-600 hover:bg-gray-100">
              Cancel
            </button>
            {update.isError && <span className="text-sm text-red-600">Failed to save. Please try again.</span>}
          </div>
        </div>
      ) : hasDescription ? (
        <div className="overflow-x-auto rounded-lg border bg-white p-5">
          <AdfRenderer adf={descriptionAdf} fallbackText={description} attachments={attachments} />
        </div>
      ) : (
        <button type="button" onClick={() => setEditing(true)} className="w-full rounded-lg border border-dashed px-3 py-2 text-left text-sm text-gray-400 hover:bg-gray-50">
          Add a description…
        </button>
      )}
    </section>
  );
}
